import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Accept': 'application/json',
  },
});

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Store token getter/setter functions (will be set by AuthContext)
let getAccessToken = () => null;
let refreshAccessToken = async () => null;
let handleLogout = () => {};

export const setAuthHandlers = (getToken, refreshToken, logout) => {
  getAccessToken = getToken;
  refreshAccessToken = refreshToken;
  handleLogout = logout;
};

// Request interceptor - attach access token to all requests
axiosInstance.interceptors.request.use(
  (config) => {

    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh on 401
axiosInstance.interceptors.response.use(
  (response) => response,

  async (error) => {

    const originalRequest = error.config;

    // Ignore if not 401, refresh endpoint, or already retried
    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest.url?.includes('/api/auth/refresh') ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If refresh already running → queue request
    if (isRefreshing) {

      return new Promise((resolve, reject) => {

        failedQueue.push({ resolve, reject });

      })
      .then(token => {

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${token}`;

        return axiosInstance(originalRequest);

      })
      .catch(err => Promise.reject(err));
    }

    isRefreshing = true;

    try {

      const newToken = await refreshAccessToken();

      if (newToken) {

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);

        return axiosInstance(originalRequest);

      } else {

        processQueue(new Error('Token refresh failed'), null);

        // Only logout if refresh endpoint returned 401
        if (error.response?.status === 401) {
          handleLogout();
        }

        return Promise.reject(error);
      }

    } catch (refreshError) {

      processQueue(refreshError, null);

      // Logout only if refresh token invalid
      if (refreshError?.response?.status === 401) {
        handleLogout();
      }

      return Promise.reject(refreshError);

    } finally {

      isRefreshing = false;

    }

  }
);

export default axiosInstance;